// scanner-fp: `useCallback` deps intentionally omit `api` (module singleton), setters (stable), and local vars. See /app/SECURITY_SCANNER_IGNORE.md §4.
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, CheckCircle2, Circle, Trash2, Clock, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

const PRIORITIES = ["low", "normal", "high", "urgent"];
const STATUSES = ["open", "assigned", "accepted", "in_progress", "closure_requested", "extension_requested", "completed", "declined", "reopened", "cancelled"];
const STATUS_COLORS = {
  open: "text-slate-500", assigned: "text-amber-600", accepted: "text-blue-600",
  in_progress: "text-blue-600", closure_requested: "text-purple-600",
  extension_requested: "text-purple-600", completed: "text-emerald-600",
  done: "text-emerald-600", declined: "text-rose-600", reopened: "text-amber-600",
  cancelled: "text-slate-500",
};

export default function Tasks() {
  const nav = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", due_at: "", priority: "normal", assigned_to: "", related_type: "", related_id: "" });

  const load = useCallback(async () => {
    const r = await api.get("/crm/tasks", { params: mineOnly ? { mine: true } : {} });
    setTasks(r.data);
  }, [mineOnly]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/crm/teammates").then(r => setTeam(r.data));
  }, []);

  const save = async () => {
    try {
      const payload = { ...form };
      if (!payload.assigned_to) delete payload.assigned_to;
      if (!payload.related_type) { delete payload.related_type; delete payload.related_id; }
      await api.post("/crm/tasks", payload);
      toast.success("Task created");
      setOpen(false);
      setForm({ title: "", description: "", due_at: "", priority: "normal", assigned_to: "", related_type: "", related_id: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setStatus = async (t, status) => {
    await api.post(`/crm/tasks/${t.id}/status`, { status });
    load();
  };
  const del = async (t) => {
    if (!window.confirm("Delete task?")) return;
    await api.delete(`/crm/tasks/${t.id}`);
    load();
  };

  // Columns collapse related statuses so the board doesn't get 10 columns wide.
  const COLUMNS = [
    { key: "assigned", label: "To do", match: ["open", "assigned", "reopened"] },
    { key: "in_progress", label: "In progress", match: ["accepted", "in_progress"] },
    { key: "pending", label: "Pending review", match: ["closure_requested", "extension_requested"] },
    { key: "completed", label: "Completed", match: ["completed", "done"] },
    { key: "declined", label: "Declined / cancelled", match: ["declined", "cancelled"] },
  ];
  const grouped = COLUMNS.map(c => ({
    status: c.key,
    label: c.label,
    items: tasks.filter(t => c.match.includes(t.status)),
  }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold">Tasks</h2>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={mineOnly} onCheckedChange={setMineOnly} data-testid="tasks-mine-toggle" /> Mine only
          </label>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="crm-new-task-btn">
          <Plus className="w-4 h-4 mr-2" /> New task
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {grouped.map(({ status, label, items }) => (
          <div key={status} className="rounded-xl border border-border bg-card p-3 min-h-[200px]" data-testid={`task-col-${status}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold">{label}</span>
              <Badge variant="secondary">{items.length}</Badge>
            </div>
            <div className="space-y-2">
              {items.map(t => (
                <div
                  key={t.id}
                  data-testid={`task-card-${t.id}`}
                  className="rounded-lg bg-muted/50 p-3 border border-border/50 space-y-1 cursor-pointer hover:bg-muted transition-colors"
                  onClick={() => nav(`/app/crm/tasks/${t.id}`)}
                >
                  <div className="flex items-start gap-2">
                    <button onClick={(e) => { e.stopPropagation(); setStatus(t, t.status === "done" ? "open" : "done"); }}>
                      {["done", "completed"].includes(t.status) ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-muted-foreground" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${["done", "completed"].includes(t.status) ? "line-through text-muted-foreground" : ""}`}>{t.title}</div>
                      {t.description && <div className="text-xs text-muted-foreground line-clamp-1">{t.description}</div>}
                      {t.due_at && (
                        <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" /> {new Date(t.due_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </div>
                  <div className="flex gap-1 flex-wrap items-center" onClick={(e) => e.stopPropagation()}>
                    <Badge variant="outline" className="text-[10px]">{t.priority}</Badge>
                    <span className={`text-[10px] font-semibold ${STATUS_COLORS[t.status] || ""}`}>
                      {(t.status || "").replace("_", " ")}
                    </span>
                    <div className="ml-auto">
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => del(t)}>
                        <Trash2 className="w-3 h-3 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {items.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">No tasks</div>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Title *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="task-form-title" /></div>
            <div><Label className="text-xs">Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Due at</Label><Input type="datetime-local" value={form.due_at} onChange={e => setForm({...form, due_at: e.target.value})} /></div>
              <div><Label className="text-xs">Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="text-xs">Assign to</Label>
                <Select value={form.assigned_to} onValueChange={v => setForm({...form, assigned_to: v})}>
                  <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>{team.map(m => <SelectItem key={m.id} value={m.id}>{m.name} ({m.role})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="task-form-save">Create task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
